/**
 * 企业端 — 新建运输单
 * C-01：支持从交易平台「发起运输」跳转预填（?order_id=X&cattle_count=N&consignor_id=S），
 * 展示来源提示条并随 createOrder 提交 order_id（后端幂等防重复）。
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createOrder, fetchDrivers, fetchVehicles } from '@/api/transit';
import { Loading } from '@/components/common';
import type { TransitDriver, TransitVehicle } from '@/types';

export default function EntOrderCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // C-01 预填参数（trade 平台「发起运输」跳转携带）
  const prefillOrderId = Number(searchParams.get('order_id')) || 0;
  const prefillCattleCount = Number(searchParams.get('cattle_count')) || 0;
  const prefillConsignorId = Number(searchParams.get('consignor_id')) || 0;

  const [vehicles, setVehicles] = useState<TransitVehicle[]>([]);
  const [drivers, setDrivers] = useState<TransitDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    consignor_id: prefillConsignorId || 3001,
    vehicle_id: 0,
    driver_id: 0,
    origin_city: '',
    destination_city: '',
    cattle_count: prefillCattleCount,
    remark: '',
  });

  useEffect(() => {
    Promise.all([fetchVehicles(), fetchDrivers()]).then(([v, d]) => {
      setVehicles(v.list);
      setDrivers(d.list);
      if (v.list[0]) setForm((f) => ({ ...f, vehicle_id: v.list[0].id }));
      if (d.list[0]) setForm((f) => ({ ...f, driver_id: d.list[0].id }));
      setLoading(false);
    });
  }, []);

  if (loading) return <Loading />;

  const set = (key: keyof typeof form, value: string | number) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    if (!form.origin_city || !form.destination_city) {
      alert('请填写起运地与目的地');
      return;
    }
    if (!form.vehicle_id || !form.driver_id) {
      alert('请选择车辆与司机');
      return;
    }
    setSubmitting(true);
    const order = await createOrder({
      order_id: prefillOrderId || undefined,
      consignor_id: form.consignor_id,
      vehicle_id: form.vehicle_id,
      driver_id: form.driver_id,
      origin_city: form.origin_city,
      destination_city: form.destination_city,
      cattle_count: form.cattle_count,
      remark: form.remark,
    });
    setSubmitting(false);
    navigate(`/enterprise/orders/${order.id}`);
  };

  return (
    <div>
      <Link to="/enterprise/orders" style={{ fontSize: 13 }}>
        ← 返回运输单列表
      </Link>
      <div className="page-title" style={{ marginTop: 8, marginBottom: 16 }}>
        新建运输单
      </div>
      {prefillOrderId > 0 ? (
        <div className="card" style={{ marginBottom: 16, background: 'var(--color-bg-soft, #f6f8fa)', padding: '12px 16px' }}>
          <span style={{ fontSize: 13 }}>
            🔗 来源：交易订单 YNJ…（订单ID {prefillOrderId}）
            <a
              href={`https://trade.yunniuji.com/orders/${prefillOrderId}`}
              target="_blank"
              rel="noreferrer"
              style={{ marginLeft: 8 }}
            >
              查看交易订单 →
            </a>
          </span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 4 }}>
            已预填牛只数量与货主，车辆/司机/起运目的地请在此补填；同一交易订单重复提交将返回既有运输单（幂等）
          </span>
        </div>
      ) : null}
      <div className="card" style={{ maxWidth: 560 }}>
        <div className="field">
          <label>起运城市 *</label>
          <input value={form.origin_city} onChange={(e) => set('origin_city', e.target.value)} placeholder="如 济南" />
        </div>
        <div className="field">
          <label>目的地城市 *</label>
          <input value={form.destination_city} onChange={(e) => set('destination_city', e.target.value)} placeholder="如 青岛" />
        </div>
        <div className="grid grid-2">
          <div className="field">
            <label>执行车辆 *</label>
            <select value={form.vehicle_id} onChange={(e) => set('vehicle_id', Number(e.target.value))}>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate_no}（{v.vehicle_type}）
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>驾驶员 *</label>
            <select value={form.driver_id} onChange={(e) => set('driver_id', Number(e.target.value))}>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}（{d.phone}）
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-2">
          <div className="field">
            <label>货主（托运企业）ID</label>
            <input type="number" min={0} value={form.consignor_id} onChange={(e) => set('consignor_id', Number(e.target.value))} />
          </div>
          <div className="field">
            <label>牛只数量（头）</label>
            <input type="number" min={0} placeholder="满载 43-45 头" value={form.cattle_count} onChange={(e) => set('cattle_count', Number(e.target.value))} />
          </div>
        </div>
        <div className="field">
          <label>备注</label>
          <textarea rows={3} value={form.remark} onChange={(e) => set('remark', e.target.value)} />
        </div>
        <div className="row">
          <button className="btn btn-primary" disabled={submitting} onClick={submit}>
            {submitting ? '提交中…' : '创建运输单'}
          </button>
          <Link className="btn btn-ghost" to="/enterprise/orders">
            取消
          </Link>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 12 }}>
          提示：创建后需上传检疫调运单并核验通过（A/B 证），方可确认起运。
        </p>
      </div>
    </div>
  );
}
